import { useEffect, useState } from 'react'
import { type Font } from './useFontLoader'

export type CustomFontDescriptor = { family: string; url: string }

/**
 * Registers custom fonts (uploaded to the workspace's moodboard) into the
 * document so the canvas font picker can list them and the canvas 2D context can
 * paint them. The live editor and the offscreen exporter both resolve fonts
 * through `document.fonts`, so adding a FontFace here is enough for both.
 *
 * We don't read weights/styles from the binary, so each uploaded file surfaces
 * as one selectable family at regular weight. Returns the successfully-loaded
 * families as `Font[]`, ready to merge into the picker.
 */
export function useCustomFonts(descriptors: CustomFontDescriptor[]): { fonts: Font[] } {
  const [loadedFamilies, setLoadedFamilies] = useState<string[]>([])

  // Re-run only when the actual font set changes — `descriptors` is a fresh
  // array on every parent render.
  const key = descriptors.map((d) => `${d.family}|${d.url}`).join('\n')

  useEffect(() => {
    // Two files can sanitize to the same family name; keep the first of each.
    const unique = [...new Map(descriptors.map((d) => [d.family, d])).values()]
    if (unique.length === 0) {
      setLoadedFamilies([])
      return
    }

    let cancelled = false
    void Promise.all(
      unique.map(async ({ family, url }) => {
        try {
          // `document.fonts` has no lookup-by-family, so scan. Avoids adding the
          // same face twice (remount, or the moodboard preview already added it).
          const alreadyAdded = Array.from(document.fonts).some((f) => f.family === family)
          if (!alreadyAdded) {
            const face = new FontFace(family, `url("${url}")`, { display: 'swap' })
            await face.load()
            document.fonts.add(face)
          }
          return family
        } catch (error) {
          console.warn(`Failed to load custom font "${family}"`, error)
          return null
        }
      }),
    ).then((families) => {
      if (!cancelled) setLoadedFamilies(families.filter((f): f is string => f !== null))
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return {
    fonts: loadedFamilies.map((fontFamily) => ({
      fontFamily,
      weights: [{ value: 400, style: 'normal' }],
    })),
  }
}
